import { useState, useEffect, createContext, useContext } from 'react'

const AppThemeContext = createContext()

export const useAppTheme = () => {
  const context = useContext(AppThemeContext)
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider')
  }
  return context
}

export const AppThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      // Check localStorage first
      const stored = localStorage.getItem('bad-company-theme')
      if (stored && (stored === 'light' || stored === 'dark')) {
        return stored
      }
      // Check system preference with improved detection
      if (window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
      }
    }
    // Default to dark theme
    return 'dark'
  })

  useEffect(() => {
    const root = document.documentElement
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark')
    
    // Add current theme class
    root.classList.add(theme)
    
    // Store in localStorage
    localStorage.setItem('bad-company-theme', theme)
    
    // Update CSS custom properties for smooth transitions
    if (theme === 'light') {
      root.style.setProperty('--bg-primary', '#ffffff')
      root.style.setProperty('--bg-secondary', '#f8fafc')
      root.style.setProperty('--bg-tertiary', '#f1f5f9')
      root.style.setProperty('--text-primary', '#0f172a')
      root.style.setProperty('--text-secondary', '#475569')
      root.style.setProperty('--text-muted', '#64748b')
      root.style.setProperty('--border-color', '#e2e8f0')
      root.style.setProperty('--border-hover', '#cbd5e1')
      root.style.setProperty('--shadow-color', 'rgba(15, 23, 42, 0.1)')
    } else {
      root.style.setProperty('--bg-primary', '#000000')
      root.style.setProperty('--bg-secondary', '#0f172a')
      root.style.setProperty('--bg-tertiary', '#1e293b')
      root.style.setProperty('--text-primary', '#f8fafc')
      root.style.setProperty('--text-secondary', '#cbd5e1')
      root.style.setProperty('--text-muted', '#94a3b8')
      root.style.setProperty('--border-color', '#334155')
      root.style.setProperty('--border-hover', '#475569')
      root.style.setProperty('--shadow-color', 'rgba(0, 0, 0, 0.5)')
    }
  }, [theme])

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: light)')
      
      const handleChange = (e) => {
        // Only auto-update if user hasn't manually set a preference
        const stored = localStorage.getItem('bad-company-theme')
        const userHasPreference = stored && (stored === 'light' || stored === 'dark')
        
        if (!userHasPreference) {
          setTheme(e.matches ? 'light' : 'dark')
        }
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  const setLightTheme = () => setTheme('light')
  const setDarkTheme = () => setTheme('dark')

  const value = {
    theme,
    setTheme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  }

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  )
}

export default AppThemeProvider